import React, { useState } from "react";
import { apiClient } from "../lib/api";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Download, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";

const EXCHANGES = [
  { value: "ALL", label: "Todas as bolsas" },
  { value: "B3", label: "B3 (Brasil)" },
  { value: "NYSE", label: "NYSE" },
  { value: "NASDAQ", label: "NASDAQ" },
];

export default function CsvExporter() {
  const [exchange, setExchange] = useState("ALL");
  const [excludePlaceholder, setExcludePlaceholder] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const params = { exclude_placeholder: excludePlaceholder };
      if (exchange !== "ALL") params.exchange = exchange;
      const res = await apiClient.get("/admin/data/export-csv", {
        params,
        responseType: "blob",
      });
      // Extract filename from Content-Disposition if present
      const disp = res.headers["content-disposition"] || "";
      const match = disp.match(/filename="?([^";]+)"?/i);
      const stamp = new Date().toISOString().slice(0, 10);
      const filename = match
        ? match[1]
        : `soiin-cotacoes-${exchange.toLowerCase()}-${stamp}.csv`;

      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Download iniciado: ${filename}`);
    } catch (e) {
      const msg =
        e.response?.status === 404
          ? "Nenhuma ação encontrada para o filtro selecionado."
          : "Falha ao exportar CSV.";
      toast.error(msg);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card data-testid="csv-exporter-card">
      <CardHeader>
        <CardTitle className="font-display text-lg flex items-center gap-2">
          <Download className="w-5 h-5" /> Exportar cotações (CSV)
        </CardTitle>
        <CardDescription>
          Gera o arquivo no mesmo formato aceito pelo upload
          (<code className="text-[11px]">Ticker;Data;Abertura;Maxima;Minima;Fechamento;Ajustado;Volume</code>),
          codificação UTF-8 com BOM e separador <code>;</code>. Pode ser reimportado sem ajustes.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">
              Bolsa
            </label>
            <Select value={exchange} onValueChange={setExchange}>
              <SelectTrigger
                className="filter-yellow"
                data-testid="csv-export-exchange-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXCHANGES.map((ex) => (
                  <SelectItem key={ex.value} value={ex.value}>
                    {ex.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground block mb-1">
              Placeholders
            </label>
            <label className="flex items-center gap-2 h-9 px-3 border border-border rounded-md bg-background cursor-pointer">
              <input
                type="checkbox"
                checked={excludePlaceholder}
                onChange={(e) => setExcludePlaceholder(e.target.checked)}
                data-testid="csv-export-exclude-placeholder"
              />
              <span className="text-sm">Excluir cotações sintéticas (placeholder)</span>
            </label>
          </div>
        </div>

        <Button
          onClick={handleDownload}
          disabled={downloading}
          data-testid="csv-export-download-button"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          {downloading ? "Preparando arquivo…" : "Baixar CSV"}
        </Button>
      </CardContent>
    </Card>
  );
}
